<template>
  <div class="login">
    <el-form
      v-if="!twoFactorPayload"
      ref="loginForm"
      :model="loginForm"
      :rules="loginRules"
      class="login-form"
      auto-complete="on"
      label-position="left"
    >
      <div class="title-container">
        <h3 class="title">Вход в административную панель</h3>
      </div>

      <el-form-item prop="username">
        <span class="svg-container">
          <svg-icon icon-class="user" />
        </span>
        <el-input
          ref="username"
          v-model="loginForm.username"
          placeholder="Имя пользователя"
          name="username"
          type="text"
          tabindex="1"
          auto-complete="on"
        />
      </el-form-item>

      <el-form-item prop="password">
        <span class="svg-container">
          <svg-icon icon-class="password" />
        </span>
        <el-input
          :key="passwordType"
          ref="password"
          v-model="loginForm.password"
          :type="passwordType"
          placeholder="Пароль"
          name="password"
          tabindex="2"
          auto-complete="on"
          @keyup.enter.native="handleLogin"
        />
        <span class="show-pwd" @click="showPwd">
          <svg-icon
            :icon-class="passwordType === 'password' ? 'eye' : 'eye-open'"
          />
        </span>
      </el-form-item>

      <el-button
        :loading="loading"
        :disabled="loading"
        type="primary"
        style="width: 100%"
        @click.native.prevent="handleLogin"
        >Войти</el-button
      >
    </el-form>
    <LoginFormPhoneVerification
      v-else
      :payload="twoFactorPayload"
      :is-retrying="twoFactorRetrying"
      @success="onTwoFactorSuccess"
      @back="onTwoFactorBack"
      @retry="onTwoFactorRetry"
    />
    <ChangePasswordModal
      :visible="showChangePasswordModal"
      @close="onCloseChangePassword"
      @password-changed="onPasswordChanged"
    />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { messages } from "@/utils/validate";
import { setRoleToken } from "@/utils/auth";
import ChangePasswordModal from "@/components/ChangePasswordModal";
import LoginFormPhoneVerification from "@/components/LoginFormPhoneVerification";

export default {
  name: "Login",
  components: {
    ChangePasswordModal,
    LoginFormPhoneVerification,
  },
  data() {
    return {
      loginForm: {
        username: process.env.NODE_ENV === "development" ? `admin` : ``,
        password: process.env.NODE_ENV === "development" ? `123qweasd` : ``,
      },
      loginRules: {
        username: [
          { required: true, trigger: "blur", message: messages.required },
        ],
        password: [
          { required: true, trigger: "blur", message: messages.required },
        ],
      },
      loading: false,
      passwordType: "password",
      redirect: undefined,
      showChangePasswordModal: false,
      twoFactorPayload: null,
      twoFactorRetrying: false,
    };
  },
  computed: {
    ...mapGetters(["mustChangePassword"]),
  },
  watch: {
    $route: {
      handler: function (route) {
        this.redirect = route.query && route.query.redirect;
      },
      immediate: true,
    },
    mustChangePassword: {
      handler(value) {
        if (value) {
          this.showChangePasswordModal = true;
        }
      },
      immediate: true,
    },
  },
  methods: {
    showPwd() {
      if (this.passwordType === "password") {
        this.passwordType = "";
      } else {
        this.passwordType = "password";
      }
      this.$nextTick(() => {
        this.$refs.password.focus();
      });
    },
    handleLogin() {
      this.$refs.loginForm.validate((valid) => {
        if (valid) {
          this.requestLogin();
        }
      });
    },
    requestLogin({ isRetry = false } = {}) {
      if (!isRetry) {
        this.loading = true;
      }
      return this.$store
        .dispatch("user/login", this.loginForm)
        .then(() => {
          if (!isRetry) {
            this.loading = false;
          }
          this.finalizeLogin();
        })
        .catch((error) => {
          if (!isRetry) {
            this.loading = false;
          }
          this.handleLoginError(error, { isRetry });
        });
    },
    handleLoginError(error, { isRetry } = {}) {
      const status =
        error && error.response ? error.response.status : undefined;
      const data = error && error.response ? error.response.data : null;

      if (status === 400 && data && data.error === "TwoFactorRequired") {
        this.twoFactorPayload = data;
        this.twoFactorRetrying = false;
        return;
      }

      if (isRetry) {
        this.twoFactorRetrying = false;
      }
    },
    async finalizeLogin() {
      const token = this.$store.state.user.token;
      this.twoFactorPayload = null;
      this.twoFactorRetrying = false;

      if (token) {
        setRoleToken(token);
      } else {
        setRoleToken("");
      }

      try {
        await this.$store.dispatch("user/getInfo");
      } catch (error) {
        this.$router.push({ path: "/login" });
        return;
      }

      if (this.mustChangePassword) {
        // Показываем модалку со сменой пароля и не даем зайти в систему.
        this.showChangePasswordModal = true;
        return;
      }

      this.$router.push({ path: this.redirect || "/" });
    },
    onTwoFactorSuccess() {
      this.finalizeLogin();
    },
    onTwoFactorBack() {
      this.twoFactorPayload = null;
      this.twoFactorRetrying = false;
    },
    onTwoFactorRetry() {
      this.twoFactorRetrying = true;
      this.requestLogin({ isRetry: true });
    },
    onCloseChangePassword() {
      this.showChangePasswordModal = false;
    },
    onPasswordChanged() {
      setTimeout(() => {
        this.$message.success("Войдите с новым паролем!");
      }, 3000);
    },
  },
};
</script>

<style lang="scss">
/* 修复input 背景不协调 和光标变色 */
/* Detail see https://github.com/PanJiaChen/vue-element-admin/pull/927 */

$bg: #283443;
$light_gray: #000;
$cursor: #000;

@supports (-webkit-mask: none) and (not (cater-color: $cursor)) {
  .login-form .el-input input {
    color: $cursor;
  }
}

/* reset element-ui css */
.login-form {
  .el-input {
    display: inline-block;
    height: 47px;
    width: 85%;

    input {
      background: transparent;
      border: 0px;
      -webkit-appearance: none;
      border-radius: 0px;
      padding: 12px 5px 12px 15px;
      color: $light_gray;
      height: 47px;
      caret-color: $cursor;

      &:-webkit-autofill {
        box-shadow: 0 0 0px 1000px #fff inset !important;
        -webkit-text-fill-color: $cursor !important;
      }
    }
  }

  .el-form-item {
    border: 1px solid #d2d2d2;
    border-radius: 3px;
    color: #454545;
  }
}
</style>

<style lang="scss" scoped>
$bg: #f5f5f5;
$dark_gray: #ccc;
$light_gray: #000;

.login {
  min-height: 100%;
  width: 100%;
  background-color: $bg;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.login-form {
  position: relative;
  // width: 520px;
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
  overflow: hidden;
  background-color: #fff;
  border-radius: 4px;
  padding: 40px 60px 60px;

  .tips {
    font-size: 14px;
    color: #999;
    margin-bottom: 10px;

    span {
      &:first-of-type {
        margin-right: 16px;
      }
    }
  }

  .svg-container {
    padding: 0;
    padding-left: 15px;
    color: $dark_gray;
    vertical-align: middle;
    display: inline-block;
    .svg-icon {
      display: block;
      margin-top: -1px;
    }
  }

  .title-container {
    position: relative;

    .title {
      font-size: 26px;
      color: $light_gray;
      margin: 0px auto 40px auto;
      text-align: center;
      font-weight: bold;
    }
  }

  .show-pwd {
    position: absolute;
    right: 10px;
    top: 7px;
    font-size: 16px;
    color: $dark_gray;
    cursor: pointer;
    user-select: none;
  }
}
</style>
